import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Copy, MessageCircle, Mail, Check, X, Trash2, Star, Video, Clapperboard } from "lucide-react";
import SocialClipDialog from "./SocialClipDialog";
import { API, getTours } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

const BASE = API.replace(/\/api$/, "");
const TABS = [["pending", "Awaiting approval"], ["approved", "Published"], ["invited", "Link sent"], ["rejected", "Rejected"]];
const linkFor = (t) => `${window.location.origin}/video-review/${t}`;

const call = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...authHeader(), ...(opts.headers || {}) } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Request failed");
  return data;
};

function ShareLink({ r }) {
  const url = linkFor(r.token);
  const msg = `Hi ${r.client_name.split(" ")[0]}, we'd love to hear about your adventure! Tap this link on your phone to record a quick video review (up to 60 seconds): ${url}`;
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="text-xs bg-sand px-2 py-1 rounded-sm truncate max-w-[260px]" data-testid={`vr-link-${r.token}`}>{url}</code>
      <button onClick={copy} data-testid={`vr-copy-${r.token}`} className="p-2 rounded-sm border border-sand-dark hover:border-moss" title="Copy link"><Copy className="w-3.5 h-3.5" /></button>
      <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid={`vr-whatsapp-${r.token}`}
        className="p-2 rounded-sm border border-sand-dark hover:border-moss" title="Send on WhatsApp"><MessageCircle className="w-3.5 h-3.5" /></a>
      <a href={`mailto:${r.client_email || ""}?subject=${encodeURIComponent("Share your adventure in a quick video")}&body=${encodeURIComponent(msg)}`}
        data-testid={`vr-email-${r.token}`} className="p-2 rounded-sm border border-sand-dark hover:border-moss" title="Send by email"><Mail className="w-3.5 h-3.5" /></a>
    </div>
  );
}

function InviteForm({ tours, onCreated }) {
  const [f, setF] = useState({ client_name: "", client_email: "", tour_slug: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.client_name.trim()) return toast.error("Add the client's name");
    setBusy(true);
    try {
      const doc = await call("/admin/video-reviews/invites", { method: "POST", body: JSON.stringify({ ...f, tour_slug: f.tour_slug || null }) });
      setF({ client_name: "", client_email: "", tour_slug: "" }); onCreated(doc); toast.success("Review link created");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const input = "h-10 px-3 rounded-sm border border-sand-dark bg-white text-sm focus:outline-none focus:border-moss";
  return (
    <div className="border border-sand-dark rounded-md bg-white p-5" data-testid="vr-invite-form">
      <h3 className="font-display font-bold text-sm mb-3">Create a review link for a client</h3>
      <div className="grid sm:grid-cols-[1fr_1fr_1.3fr_auto] gap-2">
        <input className={input} placeholder="Client name" value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} data-testid="vr-client-name" />
        <input className={input} placeholder="Email (optional)" value={f.client_email} onChange={(e) => setF({ ...f, client_email: e.target.value })} data-testid="vr-client-email" />
        <select className={input} value={f.tour_slug} onChange={(e) => setF({ ...f, tour_slug: e.target.value })} data-testid="vr-tour-select">
          <option value="">Trip (optional)</option>
          {tours.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
        </select>
        <button onClick={submit} disabled={busy} data-testid="vr-create"
          className="h-10 px-4 rounded-sm bg-moss text-white text-sm font-semibold flex items-center gap-1.5 justify-center hover:opacity-90 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create link
        </button>
      </div>
    </div>
  );
}

function ReviewRow({ r, onStatus, onDelete, onClip }) {
  const hasVideo = !!r.submitted_at;
  return (
    <div className="border border-sand-dark rounded-md bg-white p-4 flex gap-4" data-testid={`vr-row-${r.token}`}>
      <div className="w-28 shrink-0 aspect-[9/16] rounded-sm bg-ink overflow-hidden flex items-center justify-center">
        {hasVideo ? <video src={`${BASE}/api/video-reviews/${r.token}/video?key=${r.preview_key}`} controls playsInline preload="metadata"
          className="w-full h-full object-cover" data-testid={`vr-video-${r.token}`} /> : <Video className="w-6 h-6 text-white/30" />}
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-display font-bold">{r.first_name || r.client_name}</p>
            <p className="text-xs text-ink-secondary">{r.trip_name || "No trip set"}{r.client_email ? ` · ${r.client_email}` : ""}</p>
          </div>
          {r.rating && <span className="flex gap-0.5">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-terracotta text-terracotta" />)}</span>}
        </div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">
          {hasVideo ? `Sent ${new Date(r.submitted_at).toLocaleString()}` : `Link created ${new Date(r.created_at).toLocaleDateString()}`}
        </p>
        {r.status === "invited" && <ShareLink r={r} />}
        <div className="mt-auto flex flex-wrap gap-2">
          {hasVideo && r.status !== "approved" && (
            <button onClick={() => onStatus(r, "approved")} data-testid={`vr-approve-${r.token}`}
              className="h-9 px-3 rounded-sm bg-moss text-white text-sm font-semibold flex items-center gap-1.5"><Check className="w-4 h-4" /> Approve & publish</button>
          )}
          {hasVideo && r.status !== "rejected" && (
            <button onClick={() => onStatus(r, "rejected")} data-testid={`vr-reject-${r.token}`}
              className="h-9 px-3 rounded-sm border border-sand-dark text-sm font-medium flex items-center gap-1.5 hover:border-red-400"><X className="w-4 h-4" /> {r.status === "approved" ? "Unpublish" : "Reject"}</button>
          )}
          {r.status === "approved" && (
            <button onClick={() => onClip(r)} data-testid={`vr-clip-${r.token}`}
              className="h-9 px-3 rounded-sm bg-terracotta text-white text-sm font-semibold flex items-center gap-1.5"><Clapperboard className="w-4 h-4" /> Make social clip</button>
          )}
          <button onClick={() => onDelete(r)} data-testid={`vr-delete-${r.token}`}
            className="h-9 px-3 rounded-sm text-sm text-ink-secondary flex items-center gap-1.5 hover:text-red-600"><Trash2 className="w-4 h-4" /> Delete</button>
        </div>
      </div>
    </div>
  );
}

export default function VideoReviewsAdmin() {
  const [items, setItems] = useState(null);
  const [tours, setTours] = useState([]);
  const [tab, setTab] = useState("pending");
  const [clipFor, setClipFor] = useState(null);

  const load = () => call("/admin/video-reviews").then(setItems).catch((e) => { toast.error(e.message); setItems([]); });
  useEffect(() => { load(); getTours().then(setTours).catch(() => {}); }, []);

  const onStatus = async (r, status) => {
    try { await call(`/admin/video-reviews/${r.token}`, { method: "PATCH", body: JSON.stringify({ status }) }); toast.success(status === "approved" ? "Published to /reviews" : "Updated"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Delete ${r.first_name || r.client_name}'s review link and video?`)) return;
    try { await call(`/admin/video-reviews/${r.token}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); }
  };

  if (!items) return <div className="flex items-center text-ink-secondary"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading…</div>;
  const shown = items.filter((r) => r.status === tab);

  return (
    <div className="max-w-4xl flex flex-col gap-6" data-testid="video-reviews-admin">
      <div>
        <h2 className="font-display font-bold text-xl">Video reviews</h2>
        <p className="text-sm text-ink-secondary mt-1">Send a client their personal link. They record on their phone, you approve, and it appears on <a href="/reviews" target="_blank" rel="noreferrer" className="text-terracotta underline">/reviews</a> and the homepage.</p>
      </div>
      <InviteForm tours={tours} onCreated={(doc) => { setItems((xs) => [doc, ...xs]); setTab("invited"); }} />
      <div className="flex flex-wrap gap-1">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`vr-tab-${k}`}
            className={`px-3 py-2 text-sm font-medium rounded-sm transition-colors ${tab === k ? "bg-ink text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>
            {label} <span className="ml-1 opacity-60">{items.filter((r) => r.status === k).length}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        {shown.length ? shown.map((r) => <ReviewRow key={r.token} r={r} onStatus={onStatus} onDelete={onDelete} onClip={setClipFor} />)
          : <p className="text-sm text-ink-secondary" data-testid="vr-empty">Nothing here yet.</p>}
      </div>
      {clipFor && <SocialClipDialog review={clipFor} open onOpenChange={(o) => !o && setClipFor(null)} call={call} base={BASE} />}
    </div>
  );
}
